import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { customerClient } from "../api";
import { useI18n } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Check, ChevronLeft, Clock, MapPin, Package } from "lucide-react";

export default function OrderTracking() {
  const { oid } = useParams();
  const { t } = useI18n();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [qr, setQr] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await customerClient.get(`/orders/customer/${oid}`);
      setOrder(data);
      if ((data.payment_status === "pending_paypal" || data.payment_status === "pending_satispay") && !qr) {
        try {
          const { data: q } = await customerClient.get(`/orders/${oid}/payment-qr`);
          setQr(q);
        } catch { /* ignore */ }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail ? String(e.response.data.detail) : "Errore");
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line
  }, [oid]);

  const confirmDelivered = async () => {
    setConfirming(true);
    try {
      await customerClient.put(`/orders/customer/${oid}/delivered`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally {
      setConfirming(false);
    }
  };

  if (err) return (
    <div className="min-h-screen bg-deep flex items-center justify-center p-6">
      <div className="card-neon p-7 max-w-md text-center" data-testid="order-tracking-error">
        <p className="text-zinc-300">{err}</p>
        <Link to="/my-orders" className="btn-outline mt-5"><ChevronLeft className="w-4 h-4" /> {t.myOrders.back}</Link>
      </div>
    </div>
  );

  if (!order) return <div className="min-h-screen flex items-center justify-center text-zinc-500">…</div>;

  const isPickup = (order.order_type || "delivery") === "pickup";
  const flow = isPickup
    ? ["paid", "accepted", "preparing", "ready", "delivered"]
    : ["paid", "accepted", "preparing", "out_for_delivery", "delivered"];
  const stepIdx = flow.indexOf(order.status);
  const isFinal = order.status === "delivered" || order.status === "rejected" || order.status === "cancelled";
  const customerCanConfirm = (order.status === "ready" && isPickup) || (order.status === "out_for_delivery" && !isPickup);

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-10 w-full flex-1" data-testid="order-tracking-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/my-orders" className="text-xs text-zinc-400 hover:text-neon-cyan inline-flex items-center" data-testid="back-to-my-orders">
            <ChevronLeft className="w-4 h-4" /> {t.myOrders.back}
          </Link>
          {order.restaurant_slug && (
            <Link to={`/r/${order.restaurant_slug}`} className="btn-outline !py-1.5 text-xs" data-testid="back-to-restaurant-btn">
              {t.myOrders.backToRestaurant || "Torna al ristorante"}
            </Link>
          )}
        </div>

        <div className="mt-4 mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">#{order.id.slice(-6)}</div>
          <h1 className="font-heading text-3xl tracking-tight mt-2">{order.restaurant_name}</h1>
          <div className="text-zinc-400 text-sm mt-1 flex flex-wrap gap-3 items-center">
            <span className={`chip !text-[10px] !py-0.5 ${isPickup ? "chip-yellow" : "chip-cyan"}`}>{isPickup ? (t.storefront.pickup || "RITIRO") : (t.storefront.delivery || "CONSEGNA")}</span>
            {order.payment_method === "cash" && (
              <span className="chip !text-[10px] !py-0.5 border-amber-500/40 text-amber-300" data-testid="tracking-cash">{t.storefront.cashPayment || "PAGAMENTO ALL'ARRIVO"}</span>
            )}
            {order.payment_method === "paypal" && (
              <span className="chip !text-[10px] !py-0.5 border-[#0070BA]/60 text-[#0070BA]" data-testid="tracking-paypal">PAYPAL</span>
            )}
            {order.payment_method === "satispay" && (
              <span className="chip !text-[10px] !py-0.5 border-[#FF3625]/60 text-[#FF3625]" data-testid="tracking-satispay">SATISPAY</span>
            )}
            {order.eta_minutes && order.status !== "delivered" && (
              <span className="inline-flex items-center gap-1 text-neon-yellow"><Clock className="w-3.5 h-3.5" /> ETA {order.eta_minutes} min</span>
            )}
            <span className="font-mono text-xs">{new Date(order.created_at).toLocaleString()}</span>
          </div>
        </div>

        {order.status === "rejected" && (
          <div className="card-neon p-5 mt-6 border-red-500/40" data-testid="order-rejected">
            <div className="font-heading font-semibold text-red-400">{t.myOrders.rejectedTitle}</div>
            {order.reject_reason && <div className="text-sm text-zinc-400 mt-1 italic">↳ {order.reject_reason}</div>}
            <div className="text-xs text-zinc-500 mt-3 font-mono">
              {order.refund_status === "manual_required"
                ? t.myOrders.refundManual
                : order.refund_status
                ? `${t.myOrders.refundStatus}: ${order.refund_status}`
                : ""}
            </div>
          </div>
        )}

        {order.payment_status === "pending_paypal" && (
          <div className="card-neon p-5 mt-6 border-[#0070BA]/50" data-testid="paypal-instructions">
            <div className="font-heading font-semibold text-[#0070BA]">{t.myOrders.payInstructionsPaypal || "Completa il pagamento su PayPal"}</div>
            <div className="grid sm:grid-cols-[auto_1fr] gap-4 mt-3 items-center">
              {qr?.qr_data_url && (
                <img src={qr.qr_data_url} alt="PayPal QR" className="w-36 h-36 rounded-lg bg-white p-2 shrink-0" data-testid="paypal-qr" />
              )}
              <div className="space-y-2 text-sm">
                <div className="font-mono">
                  <span className="text-zinc-400">{t.myOrders.amount || "Importo"}:</span>{" "}
                  <span className="text-neon-yellow font-bold">€ {Number(order.total).toFixed(2)}</span>
                </div>
                {qr?.deep_link && (
                  <a href={qr.deep_link} target="_blank" rel="noopener" className="btn-outline !py-1.5 !px-3 text-xs !border-[#0070BA]/60 text-[#0070BA] inline-flex" data-testid="paypal-deep-link">
                    {t.myOrders.openInApp || "Apri in PayPal"} →
                  </a>
                )}
                {order.paypal_email && (
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {t.myOrders.toEmail || "Email"}: <span className="text-zinc-300 select-all" data-testid="paypal-email">{order.paypal_email}</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">{t.myOrders.pendingNote || "Il ristoratore confermerà la ricezione e procederà con l'ordine."}</p>
          </div>
        )}

        {order.payment_status === "pending_satispay" && (
          <div className="card-neon p-5 mt-6 border-[#FF3625]/50" data-testid="satispay-instructions">
            <div className="font-heading font-semibold text-[#FF3625]">{t.myOrders.payInstructionsSatispay || "Completa il pagamento su Satispay"}</div>
            <div className="grid sm:grid-cols-[auto_1fr] gap-4 mt-3 items-center">
              {qr?.qr_data_url && (
                <img src={qr.qr_data_url} alt="Satispay QR" className="w-36 h-36 rounded-lg bg-white p-2 shrink-0" data-testid="satispay-qr" />
              )}
              <div className="space-y-2 text-sm">
                <div className="font-mono">
                  <span className="text-zinc-400">{t.myOrders.amount || "Importo"}:</span>{" "}
                  <span className="text-neon-yellow font-bold">€ {Number(order.total).toFixed(2)}</span>
                </div>
                {qr?.deep_link && (
                  <a href={qr.deep_link} target="_blank" rel="noopener" className="btn-outline !py-1.5 !px-3 text-xs !border-[#FF3625]/60 text-[#FF3625] inline-flex" data-testid="satispay-deep-link">
                    {t.myOrders.openInApp || "Apri in Satispay"} →
                  </a>
                )}
                {order.satispay_handle && (
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {t.myOrders.toHandle || "Handle"}: <span className="text-zinc-300 select-all" data-testid="satispay-handle">{order.satispay_handle}</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">{t.myOrders.pendingNote || "Il ristoratore confermerà la ricezione e procederà con l'ordine."}</p>
          </div>
        )}

        {order.status !== "rejected" && (
          <div className="card-neon p-6 mt-6" data-testid="order-timeline">
            <ul className="space-y-4">
              {flow.map((st, i) => {
                const active = i <= stepIdx;
                const current = i === stepIdx;
                return (
                  <li key={st} className="flex items-start gap-3" data-testid={`order-step-${st}`}>
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border ${active ? "bg-neon-cyan text-[#06061a] border-[#00E5FF] glow-cyan" : "border-line text-zinc-600"}`}>
                      {active ? <Check className="w-3.5 h-3.5" /> : <span className="text-[10px] font-mono">{i + 1}</span>}
                    </div>
                    <div className="flex-1">
                      <div className={`font-mono uppercase tracking-widest text-xs ${current ? "text-neon-cyan" : active ? "text-zinc-300" : "text-zinc-600"}`}>
                        {t.dashboard.mark[st] || st}
                      </div>
                      {current && order.eta_minutes && order.status !== "delivered" && (
                        <div className="text-xs text-zinc-500 mt-1">ETA {order.eta_minutes} min</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {customerCanConfirm && (
          <button onClick={confirmDelivered} disabled={confirming} className="btn-primary w-full justify-center mt-6 text-sm" data-testid="confirm-delivered-btn">
            <Package className="w-4 h-4" /> {confirming ? "…" : t.myOrders.confirmDelivered}
          </button>
        )}

        <div className="card-neon p-5 mt-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-3">{t.myOrders.summary}</div>
          <ul className="text-sm text-zinc-300 space-y-1">
            {order.items.map((it, i) => (
              <li key={i} className="flex justify-between"><span>{it.qty}× {it.name}</span><span className="font-mono">€ {(it.price * it.qty).toFixed(2)}</span></li>
            ))}
          </ul>
          <div className="border-t border-line mt-3 pt-3 flex justify-between font-heading font-semibold">
            <span>{t.storefront.total}</span>
            <span className="text-neon-cyan">€ {Number(order.total).toFixed(2)}</span>
          </div>
          {!isPickup && order.address && (
            <div className="text-xs text-zinc-400 mt-3 inline-flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5" /> {order.address}</div>
          )}
        </div>

        {isFinal && (
          <Link to={`/r/${order.restaurant_slug}`} className="btn-outline w-full justify-center mt-6 text-sm" data-testid="order-back-to-restaurant">
            {t.storefront.backHome}
          </Link>
        )}
      </div>
      <Footer />
    </div>
  );
}
